import React from 'react';
import { ACTIONS } from './ProfileFormReducer';

interface ValidationErrorSummaryProps {
  validationErrors?: Record<string, string | null>;
  nestedValidationErrors?: {
    experiences?: Array<Record<string, string | null>>;
    educations?: Array<Record<string, string | null>>;
  };
  experiences?: Array<any>; // Add experiences array to calculate correct positions
  educations?: Array<any>; // Add educations array to calculate correct positions
  dispatch: any;
}

export const ValidationErrorSummary: React.FC<ValidationErrorSummaryProps> = ({
  validationErrors = {},
  nestedValidationErrors = {},
  experiences = [],
  educations = [],
  dispatch
}) => {
  const getFriendlyFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      'first_name': 'First Name',
      'last_name': 'Last Name',
      'phone': 'Phone Number',
      'zip': 'ZIP Code',
      'street_address': 'Street Address',
      'city': 'City',
      'state': 'State',
      'hourly_rate': 'Hourly Rate',
      'maximum_traveling_distance': 'Maximum Travel Distance',
      'positions': 'Position Types',
      'availability': 'Availability',
      'position_type_ids': 'Position Types',
      'job_type_ids': 'Job Types',
      'experience_or_resume': 'Experience or Resume',
      'employer': 'Employer',
      'title': 'Title',
      'start_date': 'Start Date',
      'end_date': 'End Date',
      'description': 'Description',
      'school_name': 'School Name',
      'qualification': 'Qualification',
    };

    return fieldNames[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getAllErrors = () => {
    const errors: Array<{ field: string; message: string; tab: number }> = [];
    const nestedExperienceErrors = nestedValidationErrors.experiences || [];
    const nestedEducationErrors = nestedValidationErrors.educations || [];

    const fieldToTab: Record<string, number> = {
      'first_name': 0,
      'last_name': 0,
      'phone': 0,
      'zip': 0,
      'street_address': 0,
      'city': 0,
      'state': 0,
      'experience_or_resume': 2,
      'experiences': 2,
      'educations': 2,
      'hourly_rate': 3,
      'maximum_traveling_distance': 3,
      'positions': 3,
      'availability': 3,
      'position_type_ids': 3,
      'job_type_ids': 3,
    };

    Object.keys(validationErrors).forEach(field => {
      const error = validationErrors[field];
      const shouldSkipGenericNestedErrors = (
        (field.startsWith('experiences.') && nestedExperienceErrors.length > 0) ||
        (field.startsWith('educations.') && nestedEducationErrors.length > 0)
      );

      if (!error || field === '_nested' || shouldSkipGenericNestedErrors) {
        return;
      }

      let tab = 0;
      if (fieldToTab[field] !== undefined) {
        tab = fieldToTab[field];
      } else if (field.startsWith('experiences') || field.startsWith('educations')) {
        tab = 2;
      }

      errors.push({
        field: getFriendlyFieldName(field),
        message: error,
        tab
      });
    });

    nestedExperienceErrors.forEach((experienceErrors, index) => {
      if (experiences[index]?._destroy || !experienceErrors) {
        return;
      }

      const visibleCountBefore = experiences
        .slice(0, index)
        .filter((experience) => !experience?._destroy).length;
      const displayPosition = visibleCountBefore + 1;

      Object.keys(experienceErrors).forEach(field => {
        const error = experienceErrors[field];
        if (!error) {
          return;
        }

        errors.push({
          field: `Experience ${displayPosition} - ${getFriendlyFieldName(field)}`,
          message: error,
          tab: 2
        });
      });
    });

    nestedEducationErrors.forEach((educationErrors, index) => {
      if (educations[index]?._destroy || !educationErrors) {
        return;
      }

      const visibleCountBefore = educations
        .slice(0, index)
        .filter((education) => !education?._destroy).length;
      const displayPosition = visibleCountBefore + 1;

      Object.keys(educationErrors).forEach(field => {
        const error = educationErrors[field];
        if (!error) {
          return;
        }

        errors.push({
          field: `Education ${displayPosition} - ${getFriendlyFieldName(field)}`,
          message: error,
          tab: 2
        });
      });
    });

    return errors;
  };

  const navigateToTab = (tabIndex: number) => {
    dispatch({ type: ACTIONS.SET_TAB_INDEX, payload: tabIndex });
    const element = document.getElementById("profileeditformcontainer");
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getTabName = (tabIndex: number): string => {
    const tabNames = ['General Info', 'Documents', 'Experience', 'Availability'];
    return tabNames[tabIndex] || 'Unknown';
  };

  const allErrors = getAllErrors();
  if (allErrors.length === 0) {
    return null;
  }

  const errorsByTab = allErrors.reduce((acc, error) => {
    if (!acc[error.tab]) {
      acc[error.tab] = [];
    }
    acc[error.tab].push(error);
    return acc;
  }, {} as Record<number, Array<{ field: string; message: string; tab: number }>>);

  return (
    <div className="modern-validation-summary mb-4" role="alert">
      <div className="d-flex align-items-start">
        <i className="fas fa-exclamation-triangle validation-icon"></i>
        <div className="flex-grow-1">
          <h6 className="summary-title mb-3">
            Please fix the following {allErrors.length} validation error{allErrors.length > 1 ? 's' : ''}:
          </h6>

          {Object.keys(errorsByTab).map(tabKey => {
            const tab = parseInt(tabKey, 10);
            const tabErrors = errorsByTab[tab];

            return (
              <div key={tab} className={`error-tab-section ${Object.keys(errorsByTab).length > 1 && parseInt(tabKey, 10) < Object.keys(errorsByTab).length - 1 ? 'with-divider' : ''}`}>
                <div className="tab-header mb-2">
                  <button
                    type="button"
                    className="tab-link"
                    onClick={() => navigateToTab(tab)}
                  >
                    {getTabName(tab)} ({tabErrors.length} error{tabErrors.length > 1 ? 's' : ''})
                    <i className="fas fa-external-link-alt ms-1"></i>
                  </button>
                </div>
                <ul className="error-list">
                  {tabErrors.map((error, index) => (
                    <li key={index} className="error-item">
                      <strong>{error.field}:</strong> {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
